import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  AccountBalance,
  Assessment,
  Description,
  Security,
  AccountTree,
  Gavel,
} from "@mui/icons-material";
import Layout from "../components/layout/LandingPageLayout";

const OurVision = () => {
  const theme = useTheme();

  const services = [
    {
      title: "Book Keeping",
      icon: <AccountBalance />,
      items: [
        "Recording Financial Transactions",
        "Accounts Payable Management",
        "Accounts Receivable Management",
        "Bank Reconciliation",
        "Payroll Processing",
        "Financial Reporting",
        "Tax Preparation",
        "Expense Management",
        "Inventory Management",
        "Fixed Asset Management",
        "Financial Analysis",
        "Budgeting and Forecasting",
        "Cash Flow Management",
        "Compliance and Regulatory Reporting",
        "Advisory Services",
      ],
    },
    {
      title: "Accounting",
      icon: <Assessment />,
      items: [
        "Financial statement preparation",
        "Auditing",
        "Tax planning and preparation",
        "Budgeting and forecasting",
        "Internal control review",
        "Management advisory services",
        "Business valuation",
        "Forensic accounting",
        "Cost accounting",
        "Cash flow management",
        "Merger and acquisition assistance",
        "Risk management",
        "International accounting and tax services",
        "Software implementation and training",
        "Financial analysis and reporting",
      ],
    },
    {
      title: "Reporting",
      icon: <Description />,
      items: [
        "Financial reporting",
        "Management reporting",
        "Regulatory reporting",
        "Compliance reporting",
        "Performance reporting",
        "Budget reporting",
        "Tax reporting",
        "Sustainability reporting",
        "External financial reporting",
        "Internal financial reporting",
        "Audit reporting",
        "Risk reporting",
        "Environmental reporting",
        "Social responsibility reporting",
        "Customized or ad-hoc reporting",
      ],
    },
    {
      title: "Audit",
      icon: <Security />,
      items: [
        "Financial statement audit",
        "Internal audit",
        "Compliance audit",
        "Operational audit",
        "IT audit (Information Technology)",
        "Forensic audit",
        "Tax audit support",
        "Due diligence audit",
        "Environmental audit",
        "Quality management systems audit",
        "Risk assessment and control evaluation",
        "Performance audit",
        "Special-purpose audit",
        "Fraud examination",
        "Statutory audit",
      ],
    },
    {
      title: "TAX",
      icon: <AccountTree />,
      items: [
        "Tax planning",
        "Income tax preparation for individuals",
        "Income tax preparation for businesses",
        "Estate tax planning and preparation",
        "Gift tax planning and preparation",
        "Trust tax planning and preparation",
        "Tax compliance services",
        "Tax advisory services",
        "Tax audit representation",
        "International tax planning and compliance",
        "Tax credits and incentives consulting",
        "Sales tax consulting and compliance",
        "Property tax consulting and compliance",
        "Payroll tax consulting and compliance",
        "Tax dispute resolution and litigation support",
      ],
    },
    {
      title: "Due Diligence",
      icon: <Gavel />,
      items: [
        "Financial due diligence",
        "Legal due diligence",
        "Tax due diligence",
        "Commercial due diligence",
        "Operational due diligence",
        "Environmental due diligence",
        "IT due diligence",
        "Human resources due diligence",
        "Intellectual property due diligence",
        "Regulatory due diligence",
      ],
    },
    {
      title: "Compliances",
      icon: <Security />,
      items: [
        "Regulatory compliance assessment",
        "Compliance program development",
        "Compliance monitoring and reporting",
        "Anti-money laundering (AML) compliance",
        "Know Your Customer (KYC) compliance",
        "Anti-bribery and corruption (ABC) compliance",
        "Data privacy and protection compliance",
        "Health and safety compliance",
        "Environmental compliance",
        "Labor and employment law compliance",
        "Trade compliance",
        "Financial services compliance",
        "Healthcare compliance",
        "Food safety compliance",
        "Licensing and permits compliance",
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <Layout>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: 8 }}>
        <Container maxWidth="lg">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 3,
                }}
              >
                Our Vision
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: "900px",
                  mx: "auto",
                  lineHeight: 1.8,
                  mb: 2,
                }}
              >
                Our vision is to empower businesses of all sizes with
                unparalleled efficiency, accuracy, and insight, enabling them to
                thrive in the dynamic landscape of modern commerce. By
                harnessing the transformative potential of artificial
                intelligence, we envision a future where tedious manual
                processes are replaced with seamless automation, enabling
                businesses to focus on innovation, growth, and strategic
                decision-making.
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: "900px",
                  mx: "auto",
                  lineHeight: 1.8,
                }}
              >
                Together, we aim to redefine the standards of financial
                excellence, driving success and prosperity for our clients and
                partners worldwide.
              </Typography>
            </Box>
          </motion.div>

          {/* Target Segment Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Box sx={{ mb: 6, textAlign: "center" }}>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                Explore Our Target Segments
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Discover our comprehensive range of services designed for your
                success
              </Typography>
            </Box>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={4}>
              {services.map((service, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: "100%",
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.05
                        )}, ${alpha(theme.palette.background.paper, 1)})`,
                        backdropFilter: "blur(10px)",
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )}`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: `0 12px 40px ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 3,
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          >
                            {service.icon}
                          </Box>
                          <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {service.title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {service.items.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={item}
                              size="small"
                              sx={{
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                color: theme.palette.text.primary,
                                fontSize: "0.75rem",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.15
                                  ),
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>
    </Layout>
  );
};

export default OurVision;
